import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import dynamic from "next/dynamic";
import CollectibleIcon from "@/assets/svgs/collectible.svg";
import DisplayIcon from "@/components/shared/data-display/display-icon/display-icon";
import DealTagIcon from "@/assets/svgs/deal-tag.svg";
import ChevronRightNarrow from "@/assets/svgs/chevron-right-narrow.svg";
import type {
  WorkCondition,
  WorkAuthor,
  WorkEditionDetails,
  WorkFormat,
  WorkPageData,
  WorkSeries,
  WorkSummary,
} from "@/types/work.types";
import type { SliderRecommendations } from "@/types/slider.types";
import { UserWorkEngagementStatus } from "@/types/user-work-engagement.types";
import { getWorkSelectorData, getWorkSliders } from "@/services/work.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import WorkCopySelector from "../work-copy-selector/work-copy-selector";
import WorkPurchasePanel from "../work-purchase-panel/work-purchase-panel";
import WorkPeekInside from "../work-peek-inside";
import WorkLargeCover from "../work-large-cover";
import WorkSocialLinks from "../work-social-links/work-social-links";
import { formatWorkPrice } from "../work-price";
import WorkEditionInfo from "./work-edition-info";
import WorkOverview from "./work-overview";
import WorkRating from "./work-rating";
import WorkPurchasedMessage from "./work-purchased-message";
import WorkSlider from "./work-slider";
import WorkAuthorSlider from "./work-author-slider";
import CustomerReviews from "./customer-reviews";
import {
  BlogMentions,
  CrossProductWidget,
  RelatedSubjects,
} from "./work-additional-content";
import styles from "./work-page.module.scss";
import classNames from "classnames";
import { legacyUrlString } from "@/utility/url";
import { Link } from "@/components/shared/inputs/link/link";
import Gtm, {
  GtmEvents,
  type TagManagerItemModel,
} from "@/infrastructure/observability/google-tag-manager.service";

const WorkCopyDrawer = dynamic(
  () => import("../work-copy-drawer/work-copy-drawer"),
  { ssr: false },
);

const WORK_SELECTION_CHANGE_EVENT = "work-selection-change";
const LEGACY_SELECTION_HASH_PARAMETERS = [
  "isbn",
  "edition",
  "idAmazon",
  "idiq",
  "upc",
  "ean",
] as const;
const SSR_SELECTION_QUERY_PARAMETERS = [
  "isbn",
  "edition",
  "idAmazon",
  "idiq",
] as const;

const PRODUCT_TYPE = {
  book: 1,
  video: 2,
  game: 3,
  music: 4,
  gift: 5,
} as const;

const splitNames = (names?: string) =>
  names
    ?.split(";")
    .map((name) => name.trim())
    .filter(Boolean) ?? [];

const formatNameList = (names: string[]) => {
  const visibleNames = names.slice(0, 3);

  if (visibleNames.length === 2) return visibleNames.join(" and ");

  const formatted = visibleNames.join(", ");
  return names.length > 3 ? `${formatted}, et al.` : formatted;
};

export const getWorkSliderBackgrounds = ({
  hasCrossProduct,
  hasBlogMentions,
}: {
  hasCrossProduct: boolean;
  hasBlogMentions: boolean;
}) => ({
  youMightAlsoEnjoy: !hasCrossProduct,
  recentBrowsing: !hasBlogMentions,
});

const subscribeToWorkSelection = (onStoreChange: () => void) => {
  window.addEventListener("hashchange", onStoreChange);
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(WORK_SELECTION_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("hashchange", onStoreChange);
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(WORK_SELECTION_CHANGE_EVENT, onStoreChange);
  };
};

const getWorkSelectionSnapshot = () => window.location.href;
const getServerWorkSelectionSnapshot = () => "";
const subscribeToHydration = () => () => {};
const getClientHydrationSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

export const shouldShowBackToSearchResults = (
  locationHref: string,
  referrer: string,
  historyLength: number,
): boolean => {
  if (!locationHref || !referrer || historyLength <= 1) return false;

  try {
    const location = new URL(locationHref);
    const previousLocation = new URL(referrer);
    return (
      Boolean(location.searchParams.get("resultid")?.trim()) &&
      previousLocation.pathname.toLowerCase() === "/browse/"
    );
  } catch {
    return false;
  }
};

const getBackToSearchSnapshot = () =>
  shouldShowBackToSearchResults(
    window.location.href,
    document.referrer,
    window.history.length,
  );
const getServerBackToSearchSnapshot = () => false;

const BackToSearchResults = () => {
  const previousHistoryIndex = useRef(window.history.length - 1);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      window.history.length <= 1
    ) {
      return;
    }

    event.preventDefault();
    window.history.go(previousHistoryIndex.current - window.history.length);
  };

  return (
    <Link
      href="/browse/"
      linkType="legacy"
      className={styles.backToSearch}
      onClick={handleClick}
    >
      <ChevronRightNarrow aria-hidden />
      <span>Back to Search Results</span>
    </Link>
  );
};

export interface WorkPageProps {
  /** Work and merchandising data rendered by the Works experience. */
  data: WorkPageData;
  /** Whether account-only Works features should be displayed. */
  isLoggedIn: boolean;
  /** Whether breadcrumbs should be rendered above the work details. */
  shouldDisplayBreadcrumbs?: boolean;
  /** Whether SSR chose the initial copy without an explicit route selection. */
  initialSelectionIsRecommended?: boolean;
  /** Resolution state for an SSR-visible selection query that needs URL normalization. */
  selectionQueryResolved?: boolean | null;
}

interface WorkPageContentProps {
  data: Omit<WorkPageData, "work"> & { work: WorkSummary };
  isLoggedIn: boolean;
  shouldDisplayBreadcrumbs: boolean;
  initialSelectionIsRecommended: boolean;
  selectionQueryResolved: boolean | null;
}

const SeriesParenthetical = ({ series }: { series: WorkSeries[] }) => {
  const visibleSeries = series.slice(0, 5);
  if (visibleSeries.length === 0) return null;

  if (visibleSeries.length === 1 && visibleSeries[0].number) {
    const item = visibleSeries[0];
    return (
      <p className={styles.seriesLine}>
        (Book {item.number} in the{" "}
        <Link href={item.url} linkType="legacy">
          {item.name} Series
        </Link>
        )
      </p>
    );
  }

  return (
    <p className={styles.seriesLine}>
      (Part of the{" "}
      {visibleSeries.map((item, index) => (
        <span key={`${item.idSeries}-${item.number ?? ""}`}>
          {index > 0 && (index === visibleSeries.length - 1 ? " and " : ", ")}
          <Link href={item.url} linkType="legacy">
            {item.name}
            {item.number ? ` (${item.number})` : ""} Series
          </Link>
        </span>
      ))}
      )
    </p>
  );
};

const WorkIdentifiers = ({
  condition,
  className,
}: {
  condition: WorkCondition;
  className?: string;
}) => {
  if (!condition.isbn && !condition.ean) return null;

  return (
    <div className={classNames("u-body-sm", styles.identifiers, className)}>
      {condition.isbn && (
        <div>
          ISBN: <span>{condition.isbn}</span>
        </div>
      )}
      {condition.ean && (
        <div>
          ISBN13: <span>{condition.ean}</span>
        </div>
      )}
    </div>
  );
};

const firstAvailableCondition = (format: WorkFormat): WorkCondition =>
  format.conditions.find(
    (condition) =>
      condition.idMedia === format.idMedia && condition.quantity > 0,
  ) ??
  format.fallbackCondition ??
  format.conditions[0];

/**
 * Reads a positive selection identifier from the current routing state. Legacy
 * query-string identifiers take precedence because they are reflected in SSR;
 * Legacy fragments remain a post-hydration compatibility fallback.
 * @param hashParameters Parameters encoded in the URL fragment.
 * @param queryParameters Parameters encoded in the URL query string.
 * @param name Selection parameter name to read.
 * @returns A positive integer identifier, or null when it is absent or invalid.
 */
const getPositiveLocationId = (
  hashParameters: URLSearchParams,
  queryParameters: URLSearchParams,
  name: string,
): number | null => {
  const value = Number(queryParameters.get(name) ?? hashParameters.get(name));
  return Number.isInteger(value) && value > 0 ? value : null;
};

const WorkTitleMetadata = ({
  productType,
  authors,
  edition,
}: {
  productType: number;
  authors: WorkAuthor[];
  edition: WorkEditionDetails | null;
}) => {
  const actors = splitNames(edition?.actors);
  const directors = splitNames(edition?.directors);
  const artists = splitNames(edition?.artists);
  const publishers = splitNames(edition?.publisher);
  const rating = edition?.rating?.trim();

  if (productType === PRODUCT_TYPE.video) {
    if (actors.length === 0 && directors.length === 0 && !rating) return null;

    return (
      <div className={styles.byline}>
        {actors.length > 0 && <div>Starring: {formatNameList(actors)}</div>}
        {directors.length > 0 && (
          <div>
            {directors.length > 1 ? "Directors" : "Director"}:{" "}
            {formatNameList(directors)}
          </div>
        )}
        {rating && <div>Rated: {rating}</div>}
      </div>
    );
  }

  if (productType === PRODUCT_TYPE.game) {
    if (publishers.length === 0 && !rating) return null;

    return (
      <div className={styles.byline}>
        {publishers.length > 0 && (
          <div>
            {publishers.length > 1 ? "Publishers" : "Publisher"}:{" "}
            {formatNameList(publishers)}
          </div>
        )}
        {rating && <div>Rated: {rating}</div>}
      </div>
    );
  }

  if (productType === PRODUCT_TYPE.music) {
    if (artists.length === 0 && !rating) return null;

    return (
      <div className={styles.byline}>
        {artists.length > 0 && (
          <div>
            {artists.length > 1 ? "Artists" : "Artist"}:{" "}
            {formatNameList(artists)}
          </div>
        )}
        {rating && <div>Rated: {rating}</div>}
      </div>
    );
  }

  if (productType === PRODUCT_TYPE.gift) {
    if (!edition?.brandName?.trim()) return null;

    return (
      <div className={styles.byline}>
        <Link
          href={`/browse/#b.s=mostPopular-desc&b.p=1&b.pp=30&b.oos&b.pt=5&b.f.br%5B%5D=${encodeURIComponent(edition.idBrand)}`}
          linkType="legacy"
        >
          {edition.brandName}
        </Link>
      </div>
    );
  }

  if (authors.length > 0) {
    const visibleAuthors = authors.slice(0, 3);
    return (
      <div className={styles.byline}>
        by{" "}
        {visibleAuthors.map((author, index) => (
          <span key={author.idAuthor}>
            {index > 0 && (authors.length === 2 ? " and " : ", ")}
            <Link href={author.url} linkType="legacy">
              {author.name}
            </Link>
          </span>
        ))}
        {authors.length > 3 && ", et al."}
      </div>
    );
  }

  if (artists.length === 0) return null;

  const visibleArtists = artists.slice(0, 3);
  return (
    <div className={styles.byline}>
      {artists.length > 1 ? "Bys" : "By"}:{" "}
      {visibleArtists.map((artist, index) => (
        <span key={artist}>
          {index > 0 && (artists.length === 2 ? " and " : ", ")}
          <Link
            href={`/browse/?b.search=${encodeURIComponent(artist)}#b.s=mostPopular-desc&b.p=1&b.pp=30&b.oos&b.tile`}
            linkType="legacy"
          >
            {artist}
          </Link>
        </span>
      ))}
      {artists.length > 3 && ", et al."}
    </div>
  );
};

/**
 * Restores a selection already represented in the hydrated selector. Edition and
 * IDIQ and ISBN identifiers accept both routing forms. Query values take precedence
 * so hydration retains the edition selected by the server-rendered response.
 * @param formats Selector formats embedded during SSR or loaded by the fallback.
 * @param locationSnapshot Stable browser-location snapshot observed by React.
 * @returns The requested condition when it exists in the supplied selector.
 */
const getPersistedCondition = (
  formats: WorkFormat[],
  locationSnapshot: string,
): WorkCondition | null => {
  if (!locationSnapshot) return null;

  const location = new URL(locationSnapshot);
  const hashParameters = new URLSearchParams(location.hash.slice(1));
  const queryParameters = location.searchParams;
  const idIq = getPositiveLocationId(hashParameters, queryParameters, "idiq");
  const idAmazon =
    getPositiveLocationId(hashParameters, queryParameters, "edition") ??
    getPositiveLocationId(hashParameters, queryParameters, "idAmazon");
  const isbn =
    (queryParameters.get("isbn") ?? hashParameters.get("isbn"))
      ?.trim()
      .toLocaleLowerCase() || null;
  const conditions = formats.flatMap((format) => [
    ...format.conditions,
    ...(format.fallbackCondition ? [format.fallbackCondition] : []),
  ]);
  const findNativeCondition = (
    predicate: (condition: WorkCondition) => boolean,
  ) =>
    conditions.find(
      (condition) =>
        condition.bindingIdMedia === condition.idMedia && predicate(condition),
    ) ?? conditions.find(predicate);
  const findPreferredIsbnCondition = (): WorkCondition | undefined => {
    if (!isbn) return undefined;

    const matches = conditions.filter(
      (condition) => condition.isbn.trim().toLocaleLowerCase() === isbn,
    );
    const nativeMatches = matches.filter(
      (condition) => condition.bindingIdMedia === condition.idMedia,
    );

    // An ISBN identifies an edition rather than a particular inventory copy.
    // Match Legacy by choosing that edition's first available native condition,
    // while retaining unavailable and cross-format fallbacks when no stocked
    // native condition exists.
    return (
      nativeMatches.find((condition) => condition.quantity > 0) ??
      nativeMatches[0] ??
      matches.find((condition) => condition.quantity > 0) ??
      matches[0]
    );
  };

  return (
    (idIq
      ? findNativeCondition((condition) => condition.idIq === idIq)
      : undefined) ??
    (idAmazon
      ? findNativeCondition((condition) => condition.idAmazon === idAmazon)
      : undefined) ??
    findPreferredIsbnCondition() ??
    null
  );
};

/**
 * Extracts Legacy fragment identifiers that may require a selector-only request
 * after hydration. Fragments cannot participate in SSR because browsers never send
 * them to the server. Any query selection is intentionally excluded because it was
 * resolved during SSR and takes precedence over fragment values.
 * @param locationSnapshot Stable browser-location snapshot observed by React.
 * @returns Normalized Legacy selection values, or null when no edition identifier exists.
 */
const getLegacySelection = (locationSnapshot: string) => {
  if (!locationSnapshot) return null;

  const location = new URL(locationSnapshot);
  const hashParameters = new URLSearchParams(location.hash.slice(1));
  if (
    SSR_SELECTION_QUERY_PARAMETERS.some((name) =>
      location.searchParams.has(name),
    )
  )
    return null;

  const isbn = hashParameters.get("isbn")?.trim().toLocaleLowerCase() || null;
  const idAmazon =
    getPositiveLocationId(hashParameters, location.searchParams, "edition") ??
    getPositiveLocationId(hashParameters, location.searchParams, "idAmazon");
  if (!isbn && !idAmazon) return null;

  const idIq = getPositiveLocationId(
    hashParameters,
    location.searchParams,
    "idiq",
  );
  return {
    isbn,
    idAmazon,
    idIq,
    key: `${isbn ?? ""}:${idAmazon ?? ""}:${idIq ?? ""}`,
  };
};

/**
 * Persists an interactive selection in the SSR-visible VNext query contract. Any
 * Legacy selection fragment is removed so copied URLs do not perpetuate that form;
 * unrelated query parameters and ordinary anchor fragments remain intact.
 * @param condition Exact edition condition selected by the customer.
 */
const persistCondition = (condition: WorkCondition) => {
  const parameters = new URLSearchParams(window.location.search);
  parameters.set("edition", condition.idAmazon.toString());
  parameters.delete("isbn");
  parameters.delete("idAmazon");
  if (condition.idIq) {
    parameters.set("idiq", condition.idIq.toString());
  } else {
    parameters.delete("idiq");
  }
  parameters.delete("upc");
  parameters.delete("ean");

  const query = parameters.toString();
  const hashParameters = new URLSearchParams(window.location.hash.slice(1));
  const hasLegacySelectionHash = LEGACY_SELECTION_HASH_PARAMETERS.some((name) =>
    hashParameters.has(name),
  );
  LEGACY_SELECTION_HASH_PARAMETERS.forEach((name) => {
    hashParameters.delete(name);
  });
  const remainingHash = hashParameters.toString();
  let hash = window.location.hash;
  if (hasLegacySelectionHash) {
    hash = remainingHash ? `#${remainingHash}` : "";
  }
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${hash}`,
  );
  window.dispatchEvent(new Event(WORK_SELECTION_CHANGE_EVENT));
};

const discardSelectionQuery = () => {
  const location = new URL(window.location.href);
  SSR_SELECTION_QUERY_PARAMETERS.forEach((name) =>
    location.searchParams.delete(name),
  );
  window.history.replaceState(
    window.history.state,
    "",
    `${location.pathname}${location.search}${location.hash}`,
  );
  window.dispatchEvent(new Event(WORK_SELECTION_CHANGE_EVENT));
};

const WorkPageContent = ({
  data,
  isLoggedIn,
  shouldDisplayBreadcrumbs,
  initialSelectionIsRecommended,
  selectionQueryResolved,
}: WorkPageContentProps) => {
  const { work, authors, formats: initialFormats } = data;

  const initialFormat =
    initialFormats.find((format) => format.idMedia === data.selectedMedia) ??
    initialFormats[0];
  const initialCondition =
    initialFormat.conditions.find(
      (condition) => condition.idQuality === data.selectedQuality,
    ) ?? firstAvailableCondition(initialFormat);

  const locationSnapshot = useSyncExternalStore(
    subscribeToWorkSelection,
    getWorkSelectionSnapshot,
    getServerWorkSelectionSnapshot,
  );
  const legacySelection = useMemo(
    () => getLegacySelection(locationSnapshot),
    [locationSnapshot],
  );
  const hasExplicitSelection = useMemo(() => {
    if (!locationSnapshot) return false;

    const location = new URL(locationSnapshot);
    const hashParameters = new URLSearchParams(location.hash.slice(1));
    return (
      SSR_SELECTION_QUERY_PARAMETERS.some((name) =>
        selectionQueryResolved !== false &&
        location.searchParams.has(name),
      ) ||
      LEGACY_SELECTION_HASH_PARAMETERS.some((name) => hashParameters.has(name))
    );
  }, [locationSnapshot, selectionQueryResolved]);
  // Keep the hydration render on the serialized SSR value. Browser-only URL
  // fragments can affect the heading after React commits without a mismatch.
  const hasHydrated = useSyncExternalStore(
    subscribeToHydration,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  );
  const [manuallySelectedWorkId, setManuallySelectedWorkId] = useState<
    number | null
  >(null);
  const isRecommendedSelection =
    initialSelectionIsRecommended &&
    manuallySelectedWorkId !== work.idWork &&
    (!hasHydrated || !hasExplicitSelection);
  const [selectorOverride, setSelectorOverride] = useState<{
    key: string;
    formats: WorkFormat[];
  } | null>(null);
  const requestedSelectors = useRef(new Set<string>());
  const selectorOverrideApplies =
    selectorOverride &&
    (selectorOverride.key === legacySelection?.key ||
      getPersistedCondition(selectorOverride.formats, locationSnapshot) !==
        null);
  const formats = selectorOverrideApplies
    ? selectorOverride.formats
    : initialFormats;
  const persistedCondition = useMemo(
    () => getPersistedCondition(formats, locationSnapshot),
    [formats, locationSnapshot],
  );
  const cameFromSearch = useSyncExternalStore(
    subscribeToWorkSelection,
    getBackToSearchSnapshot,
    getServerBackToSearchSnapshot,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sliders, setSliders] = useState<SliderRecommendations[]>([]);
  const visitRef = useRef<{ idWork: number; visitId: string } | null>(null);

  useEffect(() => {
    if (selectionQueryResolved == null) return;
    if (selectionQueryResolved) {
      persistCondition(initialCondition);
    } else {
      discardSelectionQuery();
    }
  }, [initialCondition, selectionQueryResolved]);

  useEffect(() => {
    if (!persistedCondition || !locationSnapshot) return;

    const location = new URL(locationSnapshot);
    const hashParameters = new URLSearchParams(location.hash.slice(1));
    if (
      !LEGACY_SELECTION_HASH_PARAMETERS.some((name) => hashParameters.has(name))
    )
      return;

    persistCondition(persistedCondition);
  }, [locationSnapshot, persistedCondition]);

  useEffect(() => {
    if (
      !legacySelection ||
      getPersistedCondition(initialFormats, locationSnapshot) ||
      requestedSelectors.current.has(legacySelection.key)
    )
      return;

    const requestedSelectorKeys = requestedSelectors.current;
    requestedSelectorKeys.add(legacySelection.key);
    let cancelled = false;
    getWorkSelectorData(work.idWork, {
      ...(legacySelection.isbn ? { isbn: legacySelection.isbn } : {}),
      ...(legacySelection.idAmazon
        ? { idAmazon: legacySelection.idAmazon }
        : {}),
      ...(legacySelection.idIq ? { idIq: legacySelection.idIq } : {}),
    })
      .then((selector) => {
        if (
          !cancelled &&
          selector &&
          getPersistedCondition(selector.formats, locationSnapshot)
        ) {
          setSelectorOverride({
            key: legacySelection.key,
            formats: selector.formats,
          });
        }
      })
      .catch((error) =>
        logError(error, {
          context: "WorkPage:legacySelectionSelector",
        }),
      );

    return () => {
      cancelled = true;
      requestedSelectorKeys.delete(legacySelection.key);
    };
  }, [initialFormats, legacySelection, locationSnapshot, work.idWork]);

  useEffect(() => {
    let cancelled = false;
    if (visitRef.current?.idWork !== work.idWork) {
      visitRef.current = {
        idWork: work.idWork,
        visitId: crypto.randomUUID(),
      };
    }

    getWorkSliders(work.idWork, visitRef.current.visitId)
      .then((result) => {
        if (!cancelled) setSliders(result);
      })
      .catch((error) => logError(error, { context: "WorkPage:sliders" }));
    return () => {
      cancelled = true;
    };
  }, [work.idWork]);

  const selectedCondition = persistedCondition ?? initialCondition;
  const selectedFormat =
    formats.find((format) => format.idMedia === selectedCondition.idMedia) ??
    initialFormat;
  const selectedSavings =
    selectedCondition.price != null &&
    selectedCondition.listPrice != null &&
    selectedCondition.listPrice > selectedCondition.price
      ? selectedCondition.listPrice - selectedCondition.price
      : null;
  const selectedTitle =
    selectedCondition.editionDetails?.title?.trim() || work.title;

  const tagManagerItem = useMemo<TagManagerItemModel>(
    () => ({
      item_name: selectedTitle,
      item_id: work.idWork,
      price: selectedCondition.price ?? 0,
      item_brand: selectedCondition.editionDetails?.publisher || null,
      item_condition: selectedCondition.name,
      item_author: authors.map((author) => author.name).join(", "),
      item_variant: selectedFormat.name,
      item_category: data.breadcrumbs.at(-1)?.name ?? "",
      quantity: 1,
      idiq: selectedCondition.idIq ?? 0,
    }),
    [
      authors,
      data.breadcrumbs,
      selectedCondition,
      selectedFormat.name,
      selectedTitle,
      work.idWork,
    ],
  );
  const trackedViewWork = useRef<number | null>(null);

  useEffect(() => {
    if (trackedViewWork.current === work.idWork) return;
    trackedViewWork.current = work.idWork;
    Gtm.sendWorkViewItemEvent(tagManagerItem);
  }, [tagManagerItem, work.idWork]);

  const handleSelectFormat = (idMedia: number) => {
    const nextFormat = formats.find((format) => format.idMedia === idMedia);
    if (!nextFormat) return;
    const nextCondition = firstAvailableCondition(nextFormat);
    setManuallySelectedWorkId(work.idWork);
    Gtm.sendWorkSelectionEvent(nextCondition.idIq, nextCondition.price);
    persistCondition(nextCondition);
  };

  const handleSelectCondition = (idQuality: number) => {
    const condition = selectedFormat.conditions.find(
      (candidate) => candidate.idQuality === idQuality,
    );
    if (!condition?.idIq) return;

    // Legacy conditions can point at the best copy in another format when the
    // selected format/quality is unavailable. Follow that link when chosen.
    const targetCondition =
      condition.idMedia === condition.bindingIdMedia
        ? condition
        : (formats
            .find((format) => format.idMedia === condition.idMedia)
            ?.conditions.find(
              (candidate) => candidate.idIq === condition.idIq,
            ) ?? condition);
    setManuallySelectedWorkId(work.idWork);
    Gtm.sendWorkSelectionEvent(targetCondition.idIq, targetCondition.price);
    persistCondition(targetCondition);
  };

  const allEditionsUrl = legacyUrlString(
    `/w/${work.cleanUrl}/${work.idWork}/all-editions/`,
  );
  const coverUrl = selectedCondition.imageUrl || work.imageUrl;
  const findSlider = (identifier: string, legacyTitlePrefix: string) =>
    sliders.find((slider) => slider.identifier === identifier) ??
    sliders.find((slider) =>
      slider.title?.toLocaleLowerCase().startsWith(legacyTitlePrefix),
    );
  const ymaeSlider = findSlider("work-ymae", "you might also");
  const authorSeriesWidget = data.authorSeriesWidget;
  const recentBrowsingSlider = findSlider(
    "work-recent-browsing",
    "based on your recent",
  );
  const hasCrossProduct = Boolean(data.crossProductWidget?.items.length);
  const hasYmaeSlider = Boolean(ymaeSlider?.items.length);
  const overviewHtml = selectedCondition.overviewHtml;
  const hasRelatedSubjects = data.relatedSubjects.length > 0;
  const hasAuthorSeriesWidget = Boolean(authorSeriesWidget?.items.length);
  const hasBlogMentions = data.blogMentions.length > 0;
  const hasRecentBrowsingSlider = Boolean(recentBrowsingSlider?.items.length);
  const sliderBackgrounds = getWorkSliderBackgrounds({
    hasCrossProduct,
    hasBlogMentions,
  });

  return (
    <div className={styles.root}>
      {shouldDisplayBreadcrumbs && (
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          {data.breadcrumbs.map((breadcrumb, index) => (
            <Fragment key={breadcrumb.url}>
              {index > 0 && (
                <ChevronRightNarrow
                  className={styles.breadcrumbChevron}
                  aria-hidden
                />
              )}
              <Link href={breadcrumb.url} linkType="legacy">
                {breadcrumb.name}
              </Link>
            </Fragment>
          ))}
        </nav>
      )}

      {cameFromSearch && (
        <div className={styles.backToSearchRow}>
          <BackToSearchResults />
        </div>
      )}

      {isLoggedIn && data.purchasedWorkMessage && (
        <WorkPurchasedMessage
          message={data.purchasedWorkMessage}
          idWork={work.idWork}
          cleanUrl={work.cleanUrl}
        />
      )}

      <article className={styles.product}>
        <div className={styles.coverColumn}>
          {selectedCondition.editionDetails?.hasFirstChapter ? (
            <WorkPeekInside
              key={selectedCondition.idAmazon}
              idAmazon={selectedCondition.idAmazon}
              title={selectedTitle}
              coverUrl={coverUrl}
              coverClassName={styles.cover}
            />
          ) : (
            <WorkLargeCover
              key={selectedCondition.idAmazon}
              title={selectedTitle}
              coverUrl={coverUrl}
              largeCoverUrl={
                selectedCondition.imageUrlExtraLarge ||
                selectedCondition.imageUrlLarge ||
                work.imageUrlLarge
              }
              coverClassName={styles.cover}
            />
          )}
          <WorkIdentifiers condition={selectedCondition} />
          <WorkSocialLinks
            cleanUrl={work.cleanUrl}
            idWork={work.idWork}
            isbn={selectedCondition.isbn}
            className={styles.coverSocialLinks}
          />
        </div>

        <div className={styles.mainColumn}>
          <header className={styles.titleBlock}>
            <h1>{selectedTitle}</h1>
            <SeriesParenthetical series={data.series} />
            <WorkTitleMetadata
              productType={work.idProductType}
              authors={authors}
              edition={selectedCondition.editionDetails}
            />
            <WorkRating
              key={work.idWork}
              value={work.rating}
              count={work.ratingCount}
            />
            {selectedCondition.quantity > selectedCondition.inCartQuantity && (
              <div className={styles.belowMediumPrice}>
                <div className={styles.belowMediumPriceLine}>
                  <strong className={styles.belowMediumCurrentPrice}>
                    {formatWorkPrice(selectedCondition.price)}
                    {selectedCondition.isThriftDeal && (
                      <DealTagIcon role="img" aria-label="Thrift Deal" />
                    )}
                  </strong>
                  {selectedCondition.listPrice != null && (
                    <span>
                      List Price:{" "}
                      <s>{formatWorkPrice(selectedCondition.listPrice)}</s>
                    </span>
                  )}
                </div>
                {selectedSavings != null && (
                  <strong className={styles.belowMediumSavings}>
                    Save {formatWorkPrice(selectedSavings)}!
                  </strong>
                )}
              </div>
            )}
            <WorkIdentifiers
              condition={selectedCondition}
              className={styles.titleIdentifiers}
            />
          </header>

          <div className={styles.selector}>
            <WorkCopySelector
              formats={formats}
              selectedFormat={selectedFormat}
              selectedCondition={selectedCondition}
              totalEditions={work.totalEditions}
              lowestEditionPrice={work.lowestEditionPrice}
              allEditionsUrl={allEditionsUrl}
              onSelectFormat={handleSelectFormat}
              onSelectCondition={handleSelectCondition}
              onOpenCopyDrawer={() => setDrawerOpen(true)}
            />
          </div>

          {work.collectibleLowPrice > 0 && (
            <section
              className={styles.otherWays}
              aria-label="Collectible editions"
            >
              <Link
                href={`/w/${work.cleanUrl}/${work.idWork}/vintage/`}
                linkType="legacy"
                className={styles.collectibleLink}
                onClick={() => Gtm.sendGtmEvent(GtmEvents.viewCollectible)}
              >
                <DisplayIcon
                  icon={CollectibleIcon}
                  className={styles.collectibleIcon}
                />
                <span className={styles.otherWaysText}>
                  <span className={styles.otherWaysLabel}>
                    Collectible editions
                    <ChevronRightNarrow
                      className={styles.otherWaysChevron}
                      aria-hidden
                    />
                  </span>
                  <small>
                    from {formatWorkPrice(work.collectibleLowPrice)}
                  </small>
                </span>
              </Link>
            </section>
          )}
        </div>

        <WorkSocialLinks
          cleanUrl={work.cleanUrl}
          idWork={work.idWork}
          isbn={selectedCondition.isbn}
          className={styles.belowSmallSocialLinks}
        />

        <div className={styles.purchase}>
          <WorkPurchasePanel
            idWork={work.idWork}
            isRecommendedSelection={isRecommendedSelection}
            initialEngagementStatus={
              work.engagementStatus ?? UserWorkEngagementStatus.None
            }
            selectedFormat={selectedFormat}
            selectedCondition={selectedCondition}
            isLoggedIn={isLoggedIn}
            tagManagerItem={tagManagerItem}
            socialLinks={(
              <WorkSocialLinks
                cleanUrl={work.cleanUrl}
                idWork={work.idWork}
                isbn={selectedCondition.isbn}
              />
            )}
          />
        </div>
      </article>

      {hasCrossProduct && (
        <CrossProductWidget widget={data.crossProductWidget} />
      )}
      {hasYmaeSlider && (
        <WorkSlider
          slider={ymaeSlider}
          hasBackground={sliderBackgrounds.youMightAlsoEnjoy}
        />
      )}
      {overviewHtml && (
        <div className={styles.details} id="condition-details">
          <WorkOverview title={work.overviewTitle} html={overviewHtml} />
        </div>
      )}

      <WorkEditionInfo
        key={selectedCondition.idAmazon}
        condition={selectedCondition}
      />
      {hasRelatedSubjects && (
        <RelatedSubjects subjects={data.relatedSubjects} />
      )}
      {hasAuthorSeriesWidget && (
        <WorkAuthorSlider
          widget={authorSeriesWidget}
          isLoggedIn={isLoggedIn}
          hasBackground
        />
      )}

      <CustomerReviews
        work={work}
        reviews={data.reviews}
        isLoggedIn={isLoggedIn}
      />

      {hasBlogMentions && (
        <BlogMentions workTitle={work.title} mentions={data.blogMentions} />
      )}
      {hasRecentBrowsingSlider && (
        <WorkSlider
          slider={recentBrowsingSlider}
          hasBackground={sliderBackgrounds.recentBrowsing}
        />
      )}

      <WorkCopyDrawer
        open={drawerOpen}
        formats={formats}
        selectedFormat={selectedFormat}
        selectedCondition={selectedCondition}
        onSelectFormat={handleSelectFormat}
        onSelectCondition={handleSelectCondition}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

const WorkPage = ({
  data,
  isLoggedIn,
  shouldDisplayBreadcrumbs = true,
  initialSelectionIsRecommended = true,
  selectionQueryResolved = null,
}: WorkPageProps) => {
  if (!data.work || data.formats.length === 0) return null;

  return (
    <WorkPageContent
      data={{ ...data, work: data.work }}
      isLoggedIn={isLoggedIn}
      shouldDisplayBreadcrumbs={shouldDisplayBreadcrumbs}
      initialSelectionIsRecommended={initialSelectionIsRecommended}
      selectionQueryResolved={selectionQueryResolved}
    />
  );
};

export default WorkPage;
